<script setup lang="ts">
import CreateOrUpdateModalContent from "@/components/Department/CreateOrUpdateModalContent.vue"
import getDepartmentAllParams from "@/types/responses/getDepartmentAllParams"
import getOperationOffice from "@/types/responses/getOperationOffice"
import getDepartmentResponse from "@/types/responses/getDepartment"
import RegisterModal from "@/components/Common/RegisterModal.vue"
import { DEPARTMENT_SELECTABLE_LEVEL } from '@/services/consts'
import { isSecondLevelDepartment } from "@/services/functions"
import { scrollToErrorComponent } from "@/services/validators"
import { Ref, ref, computed, onMounted } from "vue"
import Api from "@/services/api/ApiServiceFabrick"
import i18n from "@/lang/i18n"

const props = defineProps<{
  department: getDepartmentAllParams | null
  modelValue: boolean
  parentIdExistsDepartments: getDepartmentAllParams[]
  isDepartment: boolean
  isChildDepartment: boolean
  loginOrganizationId?: number
  updateAuthority: boolean
  closeModal: (isReacquire: boolean) => void
  showFlash: (message: string, type: string, fadeout: boolean) => { value: any }
}>()

type formType = {
  operation_office: getOperationOffice | null
  level: { key: number, label: string }
  parent: getDepartmentResponse | null
  id_for_customer: string
  name: string
}

const emit = defineEmits(['update:modelValue']);
const form = ref({
  operation_office: null,
  level: DEPARTMENT_SELECTABLE_LEVEL[0],
  parent: null,
  id_for_customer: props.department?.id_for_customer ?? "",
  name: props.department?.name ?? ""
} as formType)

const isCreateOrUpdated: Ref<boolean> = ref(false)
const errors: Ref<any> = ref({})

const modelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit("update:modelValue", value);
  },
})

onMounted(() => {
  form.value.level = setInitialLevel()
})

const title = computed(() => {
  return props.department?.id ? i18n.global.t('pages.departments.createOrUpdateModal.updateTitle') : i18n.global.t('pages.departments.createOrUpdateModal.createTitle')
})

const isDisplayExecBtn = computed(() => {
  if (props.department?.id) return props.updateAuthority
  return true
})

const departmentRequired = computed(() => {
  return props.isChildDepartment || (props.isDepartment && (!props.department?.id || props.department?.id != props.loginOrganizationId))
})

function setInitialLevel() {
  if (departmentRequired.value || props.department?.parent_id) return DEPARTMENT_SELECTABLE_LEVEL[1]
  return DEPARTMENT_SELECTABLE_LEVEL[0]
}

function createOrUpdate() {
  errors.value = {}
  isCreateOrUpdated.value = true
  const id = props.department?.id ?? null
  Api().createOrUpdateDepartment(id, requestParams()).then(() => {
    props.showFlash(i18n.global.t('pages.common.messages.success'), 'success', true)
    props.closeModal(true)
  }).catch((error) => {
    isCreateOrUpdated.value = false
    errorHandle(error.response)
  })
}

function requestParams() {
  return {
    operation_office_id: form.value.operation_office?.id ?? null,
    level: form.value.level.key,
    parent_id: isSecondLevelDepartment(form.value.level.key) ? form.value.parent?.id : null,
    id_for_customer: form.value.id_for_customer,
    name: form.value.name
  }
}

function errorHandle (res: any) {
  switch (res.status) {
    case 403:
      location.reload();
      break
    case 422:
      errors.value = res.data.errors
      scrollToErrorComponent();
  }
}
</script>
<template>
  <RegisterModal
    v-model="modelValue"
    modalMaxWidth="45%"
    :title="title"
  >
    <template v-slot:content>
      <div class="d-flex justify-center">
        <div
          class="w-75 content-center"
        >
          <CreateOrUpdateModalContent
            v-model="form"
            :department="props.department"
            :parentIdExistsDepartments="props.parentIdExistsDepartments"
            :initialOperationId="undefined"
            :isDepartment="props.isDepartment"
            :isChildDepartment="isChildDepartment"
            :loginOrganizationId="props.loginOrganizationId"
            :isRequired="departmentRequired"
            :errors="errors"
          />
        </div>
      </div>
    </template>
    <template v-slot:buttons>
      <v-btn
        class="ma-2 reset-btn"
        @click="closeModal(false)"
      >
        {{ i18n.global.t('common.closeBtn') }}
      </v-btn>
      <v-btn
        v-if="isDisplayExecBtn"
        class="ma-2 exec-btn"
        :loading="isCreateOrUpdated"
        @click="createOrUpdate"
      >
        {{ i18n.global.t('pages.common.button.conservation') }}
      </v-btn>
    </template>
  </RegisterModal>
</template>